// The one gate for every chat line (human, AI visitor, AI doorkeeper, tutorial). Strict printable ASCII:
// the only change ever made is that twelve apostrophe-like marks become ' and five quote-like marks become ".
// If anything is then outside U+0020 to U+007E (accents, emoji, controls, look-alikes, everything) the whole line is
// REFUSED and '' comes back. Nothing is folded, stripped, converted or dropped, so what is judged is what was typed.

// The one refusal text, for the server and the client alike.
export const PLAIN_NOTICE = 'Plain English letters only';

const APOSTROPHE = /[‘’‚‛′‵ʹʻʼ՚ꞌ´]/gu;
const QUOTE = /[“”„‟″]/gu;

export function plainChat(text) {
  const mapped = String(text ?? '').replace(APOSTROPHE, "'").replace(QUOTE, '"');
  return /[^\x20-\x7e]/.test(mapped) ? '' : mapped;
}

// Kept for older callers and tests: the same gate.
export const normaliseChat = plainChat;

// True when the line has something in it but the gate refused it (a blank line is not refused, it is just blank).
export const refuses = text => !isBlank(text) && plainChat(text) === '';

// Blank means empty or plain ASCII spaces only. NBSP, U+3000, BOM, tab and newline are characters, so they are refused, not ignored.
export const isBlank = text => /^ *$/.test(String(text ?? ''));

// Sprint 26 (roadmap 56): every AI line is a whole sentence. A line without an ending mark gets one: a question for a
// wh-word or an aux verb + pronoun ("can you open up"), a full stop for the rest. Trailing commas and dashes are dropped first.
const QUESTION_START = /^(who|whom|whose|what|why|how|where|when|which|(can|could|will|would|may|is|are|do|does|did|should|shall)\s+(i|you|we|it|there|this|that|he|she|they|someone|anyone)\b)/i;
export function endSentence(text) {
  const t = String(text ?? '').trim().replace(/[\s,;:-]+$/, '');
  if (!t || /[.?!]$/.test(t)) return t;
  return t + (QUESTION_START.test(t) ? '?' : '.');
}

// The tidy step for every AI line, run after plainChat and after messify: no space before a mark, one ending mark ('?' wins),
// a trailing "..." becomes ".", and a line with no letter at all is empty (so it is asked for again). Then endSentence.
export function tidyLine(text) {
  let t = String(text ?? '').trim().replace(/\s+([.?!,;])/g, '$1');
  const run = t.match(/[.?!…]+$/);
  if (run) t = t.slice(0, -run[0].length).replace(/[\s,;:-]+$/, '') + (run[0].includes('?') ? '?' : run[0].includes('!') ? '!' : '.');
  return /[a-z]/i.test(t) ? endSentence(t) : '';
}

export const MAX_CHARS = 200;
export const LONG_NOTICE = `That line is too long (${MAX_CHARS} characters at most)`;
